import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { UserCircle, ShieldCheck, FileText, Trash2, Loader2, AlertTriangle, Crown, Download, DoorOpen, Phone, PhoneCall, Sofa, Store } from "lucide-react";
import { NotificationsCard } from "@/components/NotificationsCard";

const CHANNEL_ICONS = { door_to_door: DoorOpen, phone_cold: Phone, phone_followup: PhoneCall, in_home: Sofa, retail: Store };

function ProductCard() {
  const { user, refreshUser } = useAuth();
  const [value, setValue] = useState(user?.product || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setValue(user?.product || ""); }, [user?.product]);
  const save = async () => {
    setSaving(true);
    try { await api.patch("/auth/profile", { product: value }); await refreshUser(); toast.success("Product saved — every customer will now react to it"); }
    catch { toast.error("Couldn't save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="card-surface rounded-2xl p-6" data-testid="account-product-card">
      <h2 className="font-semibold text-slate-900">What do you sell?</h2>
      <p className="text-sm text-slate-600 mt-1">Your default product or service. AI customers raise objections about <em>this</em>, and Linden's coaching uses it in every example.</p>
      <div className="flex gap-2 mt-3">
        <Input value={value} onChange={(e) => setValue(e.target.value)} maxLength={200} placeholder="e.g. Solar panels with battery storage"
          className="flex-1 bg-slate-50 border-slate-200 text-slate-900 h-11" data-testid="account-product-input" />
        <Button onClick={save} disabled={saving || value === (user?.product || "")} className="h-11 bg-emerald-500 hover:bg-emerald-600 text-black font-semibold" data-testid="account-product-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
        </Button>
      </div>
    </div>
  );
}

function ChannelCard() {
  const { user, refreshUser } = useAuth();
  const [channels, setChannels] = useState([]);
  const [saving, setSaving] = useState(null);
  useEffect(() => { api.get("/channels").then((r) => setChannels(r.data)).catch((err) => console.warn("[Account] request failed", err)); }, []);

  const choose = async (id) => {
    if (id === user?.primary_channel) return;
    setSaving(id);
    try {
      await api.patch("/auth/profile", { primary_channel: id });
      await refreshUser();
      toast.success("Default sales channel updated");
    } catch { toast.error("Couldn't save your channel"); }
    finally { setSaving(null); }
  };

  return (
    <div className="card-surface rounded-2xl p-6" data-testid="account-channel-card">
      <h2 className="font-semibold text-slate-900">How do you sell?</h2>
      <p className="text-sm text-slate-600 mt-1">Your primary channel. Every AI customer, Linden's coaching and your training plan are framed around it — you can still switch per session.</p>
      <div className="grid sm:grid-cols-2 gap-2 mt-4">
        {channels.map((c) => {
          const Icon = CHANNEL_ICONS[c.id] || Store;
          const active = user?.primary_channel === c.id;
          return (
            <button key={c.id} onClick={() => choose(c.id)} disabled={!!saving}
              className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${active ? "border-emerald-500 bg-emerald-500/10" : "border-slate-200 bg-white hover:border-emerald-300"}`}
              aria-pressed={active} data-testid={`account-channel-${c.id}`}>
              <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${active ? "bg-emerald-500 text-black" : "bg-slate-100 text-slate-600"}`}>
                {saving === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
              </span>
              <span className="text-sm font-medium text-slate-800">{c.label}</span>
              {active && <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-emerald-700">Default</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ExportCard() {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/auth/export", { responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="?([^"]+)"?/)?.[1] || "pitchcraft-data.json";
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data export has downloaded.");
    } catch { toast.error("Couldn't prepare your export. Try again."); }
    finally { setBusy(false); }
  };
  return (
    <div className="card-surface rounded-2xl p-6" data-testid="account-export-card">
      <h2 className="font-semibold text-slate-900 flex items-center gap-2"><Download className="w-4 h-4 text-emerald-600" /> Download your data</h2>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">
        Get a copy of everything Pitch Craft holds about you — profile, every roleplay conversation with its scores and feedback, your coaching chat, training plan, team, battles, tournament entries and payment history — as a single JSON file.
      </p>
      <Button onClick={download} disabled={busy} variant="outline" className="mt-4 border-slate-300 bg-white text-slate-900 hover:bg-emerald-50 hover:border-emerald-300" data-testid="export-data-btn">
        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Download className="w-4 h-4 mr-1.5" />} {busy ? "Preparing…" : "Download my data (.json)"}
      </Button>
    </div>
  );
}

function DeleteAccountDialog({ open, onOpenChange }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const ready = password.length > 0 && confirm.trim().toUpperCase() === "DELETE";

  const run = async () => {
    setBusy(true);
    try {
      await api.delete("/auth/account", { data: { password, confirm } });
      toast.success("Your account and data have been deleted.");
      logout();
      navigate("/", { replace: true });
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Couldn't delete your account");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setPassword(""); setConfirm(""); } }}>
      <DialogContent className="bg-white border-slate-200 text-slate-900" data-testid="delete-account-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600"><AlertTriangle className="w-5 h-5" /> Delete your account?</DialogTitle>
          <DialogDescription>
            This permanently removes your profile, all roleplay and coaching history, scores, XP, badges, training plan and team membership. Active subscriptions are cancelled. This cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-slate-600 text-xs">Confirm your password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
              className="mt-1 bg-slate-50 border-slate-200 text-slate-900" data-testid="delete-account-password" />
          </div>
          <div>
            <Label className="text-slate-600 text-xs">Type <span className="font-mono font-bold">DELETE</span> to confirm</Label>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="DELETE"
              className="mt-1 bg-slate-50 border-slate-200 text-slate-900 font-mono" data-testid="delete-account-confirm" />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-slate-300 bg-white text-slate-900" data-testid="delete-account-cancel-btn">Keep my account</Button>
          <Button onClick={run} disabled={!ready || busy} className="bg-red-600 hover:bg-red-700 text-white font-semibold" data-testid="delete-account-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Trash2 className="w-4 h-4 mr-1.5" /> Delete permanently</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Account() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === "admin";

  return (
    <div className="space-y-6 max-w-3xl" data-testid="account-page">
      <div>
        <p className="text-emerald-600 font-mono text-xs uppercase tracking-widest">Settings</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-1">Your account</h1>
      </div>

      <div className="card-surface rounded-2xl p-6 flex items-center gap-4" data-testid="account-profile-card">
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center text-black font-bold text-xl">
          {user?.name?.[0]?.toUpperCase() || "U"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-900 text-lg truncate" data-testid="account-name">{user?.name}</div>
          <div className="text-sm text-slate-500 truncate" data-testid="account-email">{user?.email}</div>
          <div className="flex items-center gap-2 mt-1.5 text-xs">
            <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">Level {user?.level || 1}</span>
            <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-700 border border-amber-500/20 inline-flex items-center gap-1"><Crown className="w-3 h-3" /> {user?.plan || "Free"}</span>
          </div>
        </div>
        <UserCircle className="w-6 h-6 text-slate-300 hidden sm:block" />
      </div>

      <ProductCard />

      <ChannelCard />

      <NotificationsCard />

      <div className="card-surface rounded-2xl p-6" data-testid="account-legal-card">
        <h2 className="font-semibold text-slate-900">Legal</h2>
        <div className="mt-3 flex flex-wrap gap-3">
          <Link to="/privacy" className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-emerald-700 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200" data-testid="account-privacy-link"><ShieldCheck className="w-4 h-4" /> Privacy Policy</Link>
          <Link to="/terms" className="inline-flex items-center gap-2 text-sm text-slate-700 hover:text-emerald-700 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200" data-testid="account-terms-link"><FileText className="w-4 h-4" /> Terms of Service</Link>
        </div>
      </div>

      <ExportCard />

      <div className="rounded-2xl p-6 border border-red-200 bg-red-50/60" data-testid="account-danger-zone">
        <h2 className="font-semibold text-red-700 flex items-center gap-2"><Trash2 className="w-4 h-4" /> Delete account</h2>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">
          Permanently delete your account and all associated data — conversations, scores, XP, badges, training plan and team membership. Any active subscription is cancelled. Payment records are kept in de-identified form only where the law requires it.
        </p>
        {isAdmin ? (
          <p className="text-xs text-slate-500 mt-3" data-testid="account-admin-note">Admin accounts can't be deleted from the app.</p>
        ) : (
          <Button onClick={() => setOpen(true)} variant="outline" className="mt-4 border-red-300 bg-white text-red-700 hover:bg-red-600 hover:text-white hover:border-red-600" data-testid="delete-account-btn">
            <Trash2 className="w-4 h-4 mr-1.5" /> Delete my account
          </Button>
        )}
      </div>

      <DeleteAccountDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
